import { Dispatch, SetStateAction } from "react";
import { callInference, InferenceType } from "@/lib/playground-utils";
import { Capability } from "./prompt-types";
import type { PromptOptions } from "@/components/playground/prompt-caption";

const STREAM_ERROR_MESSAGE = "Unable to stream caption. Please try again.";

type StreamCaptionInferenceArgs = {
  inferenceUrl: string;
  query: string;
  imageFile: File;
  uploadedImage: string;
  promptOptions: PromptOptions;
  reasoningEnabled: boolean;
  variant?: string;
  setInferences: Dispatch<SetStateAction<InferenceType[]>>;
};

const updateInferenceById =
  (
    setInferences: Dispatch<SetStateAction<InferenceType[]>>,
    inferenceId: number,
  ) =>
  (update: Partial<InferenceType>) => {
    setInferences((prev) =>
      prev.map((item) =>
        item.id === inferenceId ? { ...item, ...update } : item,
      ),
    );
  };

export async function streamCaptionInference({
  inferenceUrl,
  query,
  imageFile,
  uploadedImage,
  promptOptions,
  reasoningEnabled,
  variant,
  setInferences,
}: StreamCaptionInferenceArgs) {
  const captionLengthValue = promptOptions.length || "Normal";
  const inferenceId = Date.now();
  const applyUpdate = updateInferenceById(setInferences, inferenceId);

  setInferences((prev) => [
    {
      id: inferenceId,
      image: uploadedImage,
      imageFile,
      result: "",
      capability: Capability.Caption,
      query,
      captionLength: captionLengthValue,
      status: "pending",
    },
    ...prev,
  ]);

  try {
    await callInference(
      inferenceUrl,
      Capability.Caption,
      query,
      imageFile,
      uploadedImage,
      { ...promptOptions, reasoning: reasoningEnabled },
      variant,
      {
        onChunk: (caption) =>
          applyUpdate({
            result: caption,
            status: "streaming",
          }),
        onComplete: (inference) =>
          applyUpdate({
            result:
              typeof inference.result === "string" ? inference.result : "",
            duration: inference.duration,
            reasoning: inference.reasoning,
            captionLength: captionLengthValue,
            status: inference.status ?? "done",
          }),
        onError: () =>
          applyUpdate({
            result: STREAM_ERROR_MESSAGE,
            status: "error",
          }),
      },
    );
  } catch (error) {
    applyUpdate({ result: STREAM_ERROR_MESSAGE, status: "error" });
    throw error;
  }
}
